import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { BadgeBanner } from '@frontend/components';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { Button, Text, TextLink } from '@frontend/design-system';
import { WEAVE_HELP_URLS } from '../../constants';

const BANNER_STORAGE_KEY = 'schedule-banner-availability';
const DISMISS_EVENT_NAME = 'schedule-settings:availability-rules:availability-banner:dismiss-button';

const getStorageKey = (baseKey: string, userId: string, locationIds: string[]) => {
  return `${baseKey}-${userId}-${[...locationIds].sort().join('-')}`;
};

export const NewAvailabilityRulesExperienceBadgeBanner = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('schedule');
  const [isDismissed, setIsDismissed] = useState(true); // default to dismissed until we check localStorage
  const { selectedLocationIds } = useAppScopeStore();
  const userId = getUser()?.userID || 'unknown-user';
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { isIntegratedOffice, isLoading } = DataSourcesQueries.useGetAccessibleIntegrations();

  const handleDismiss = () => {
    const storageKey = getStorageKey(BANNER_STORAGE_KEY, userId, selectedLocationIds);
    localStorage.setItem(storageKey, 'true');
    setIsDismissed(true);

    pendo.track(DISMISS_EVENT_NAME, {
      locationId,
      userId,
    });
  };

  useEffect(() => {
    const storageKey = getStorageKey(BANNER_STORAGE_KEY, userId, selectedLocationIds);
    const stored = localStorage.getItem(storageKey);
    setIsDismissed(stored === 'true');
  }, [userId, selectedLocationIds]);

  if (isDismissed || isLoading) {
    return null;
  }

  return (
    <BadgeBanner styles={{ width: '100%' }}>
      <div css={contentStyles}>
        <BadgeBanner.AnimatedBadge label={t('New')} />
        <div css={{ flex: 1 }}>
          {!isV3OperatorySupported && (
            <Text size='medium' weight='bold'>
              {t('New look, same data.')}
            </Text>
          )}
          <Text size='medium'>
            {isV3OperatorySupported ? (
              t(
                'You now have access to Operatory Availability! To use it, switch “Availability Calculated By” to Operatory, then review your availability to make sure everything looks right.'
              )
            ) : (
              <>
                <Trans t={t}>
                  We've updated your scheduling availability settings with new tools to define providers, services,
                  availability rules, and booking controls from the ground up. Your existing data is preserved and
                  nothing needs to change.{' '}
                  <TextLink
                    to={
                      isIntegratedOffice
                        ? WEAVE_HELP_URLS.SCHEDULE_SETTINGS_INTEGRATED
                        : WEAVE_HELP_URLS.SCHEDULE_SETTINGS_NON_INTEGRATED
                    }
                    target='_blank'
                  >
                    Learn what's new →
                  </TextLink>
                </Trans>
              </>
            )}
          </Text>
        </div>
        <Button iconName='x-small' variant='secondary' onClick={handleDismiss} label={t('Dismiss')} />
      </div>
    </BadgeBanner>
  );
};

const contentStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-md);
`;
